import React from "react";
import { Avatar } from "../components/Avatar";
import { EAge, IFamilyMember } from "../models/IFamily";
import { ELCID, IMediaToolkitNavPage, IPromisePoint } from "../models/IMediaToolkit";
import { useNavigate } from "react-router-dom";
import { MdEmail, MdPrint, MdSms } from "react-icons/md";
import AAPService from "../services/AAPService";
import { ITranslations } from "../data/strings";

export interface IFinishPageProps extends IMediaToolkitNavPage {
    allowEmail: boolean;
    isOnline: boolean;
    planid: string;
    globalFooter: string;
    scienceTitle: string;
    talktoTitle: string;
    translations: ITranslations;
}

export const FinishPage: React.FunctionComponent<IFinishPageProps> = (props) => {
    // bail if no family data is filled out
    const navigate = useNavigate();

    React.useEffect(() => {
        if (props.family.name === "" || props.family.members[0].name === "") {
            navigate("/family");
        }
    });

    const planUrl = `https://${`${window.location.host}/${window.location.pathname}?planid=${props.planid}`.replace(
        /\/\//g,
        "/"
    )}`;
    const [activeMember, setActiveMember] = React.useState<IFamilyMember>(props.family.members[0] || {});
    const [emailRecipient, setEmailRecipient] = React.useState<string>("");
    const [sendingEmail, setSendingEmail] = React.useState<boolean>(false);
    const [emailSuccess, setEmailSuccess] = React.useState<boolean>(false);
    const [emailError, setEmailError] = React.useState<boolean>(false);

    const pointIsDefault = (member: IFamilyMember, point: IPromisePoint) => {
        return (
            (point.targetAges || []).indexOf(member.age as EAge) > -1 ||
            (point.targetAges || []).indexOf(EAge.General) > -1
        );
    };

    const shouldPointBeSelected = (member: IFamilyMember, point: IPromisePoint) => {
        return (
            (member.pointsSelected || []).indexOf(point.id) > -1 ||
            (pointIsDefault(member, point) && (member.pointsRemoved || []).indexOf(point.id) === -1)
        );
    };

    const sendEmail = async () => {
        if (emailRecipient && emailRecipient.trim() !== "") {
            setSendingEmail(true);
            setEmailSuccess(false);
            setEmailError(false);

            try {
                const emailResult = await AAPService.EmailPlan(planUrl, emailRecipient);
                console.log("emailResult", emailResult);
                setEmailSuccess(true);

                setTimeout(() => setEmailSuccess(false), 3000);
            } catch (er) {
                console.log("Error sending email", er);
                setEmailError(true);
                setEmailSuccess(false);
            }

            setSendingEmail(false);
        }
    };

    const validateEmail = (str: string): boolean => {
        if (typeof str === "undefined" || !str || str.trim() === "" || !isStringValidEmail(str)) {
            return false;
        }

        return true;
    };

    const isStringValidEmail = (str: string): boolean => {
        var reg = /^[A-Z0-9._%+-]+@([A-Z0-9-]+\.)+[A-Z]{2,4}$/i;
        return reg.test(str);
    };

    React.useEffect(() => {
        setActiveMember(props.family.members[0] || {});
    }, [props.family]);

    return (
        <>
            <div className="card-header bg-aap-creamy-gray-500 text-aap-dk-red-500 px-5 xl:px-10 py-5 pt-10 print:hidden">
                <div>
                    <h1 className="text-4xl mb-5">{props.title}</h1>
                    <div className="text-sm" dangerouslySetInnerHTML={{ __html: props.description }}></div>
                </div>
            </div>
            <div className="card-body bg-aap-cream-500 p-5 pt-0 xl:p-10 xl:pt-0">
                {props.ctaMap}
                <div
                    className={`mediaButtons mb-5 border-b border-aap-gray-200 lg:flex pb-5 ${
                        props.readonly ? `justify-center` : ``
                    } print:hidden`}
                >
                    <div
                        className={`printButtonWrap lg:basis-1/2 ${
                            !props.readonly ? `lg:border-r border-aap-gray-200` : ``
                        } lg:p-5`}
                    >
                        <button
                            type="button"
                            className="bg-aap-orange-500 hover:bg-aap-orange-600 py-3 px-5 mb-3 rounded text-white w-full block"
                            onClick={() => window.print()}
                            id={`btn_printMediaPlan_${ELCID[props.lcid]}`}
                        >
                            <MdPrint className="inline" size={24} />
                            {props.translations.printButton}{" "}
                            {props.translations.userMediaPlanTitle.replace("{{FAMILYNAME}}", props.family.name)}
                        </button>
                        <p className="p-2 text-center text-xs">{props.translations.printDescription}</p>
                        <hr className="border-aap-gray-200 py-3 lg:hidden" />
                    </div>
                    {props.allowEmail && !props.readonly ? (
                        <div className="email lg:basis-1/2 lg:p-5">
                            <div>
                                <div className="text-xs mb-1">{props.translations.emailMediaPlanTo}</div>
                                <div className="emailAddr flex">
                                    <input
                                        id={`emailAddressRecipient`}
                                        value={emailRecipient}
                                        onChange={(ev) => {
                                            setEmailError(false);
                                            setEmailSuccess(false);
                                            setEmailRecipient(ev.target.value);
                                        }}
                                        type="text"
                                        className="rounded-l-sm border border-aap-gray-200 focus:border-aap-green-500 text-lg p-2 w-full transition-all h-12 flex-grow"
                                    />
                                    <button
                                        type="button"
                                        className={`bg-aap-orange-500 hover:bg-aap-orange-600 py-3 px-5 mb-3 rounded-r-sm text-white block h-12 whitespace-nowrap disabled:bg-aap-gray-400 disabled:hover:bg-aap-gray-400`}
                                        disabled={sendingEmail || !validateEmail(emailRecipient)}
                                        id={`btn_emailMediaPlan_${ELCID[props.lcid]}`}
                                        onClick={async () => {
                                            await sendEmail();
                                            setEmailRecipient("");
                                        }}
                                    >
                                        <MdEmail className="inline" size={24} />{" "}
                                        {sendingEmail ? props.translations.sending : props.translations.send}
                                    </button>
                                </div>
                                <div className="text-aap-red-500 text-sm mb-3">
                                    {emailRecipient && !isStringValidEmail(emailRecipient)
                                        ? props.translations.enterValidEmail
                                        : null}
                                    {emailError ? props.translations.errorSendingEmail : null}
                                    {emailSuccess ? props.translations.emailSuccess : null}
                                </div>
                            </div>
                            <hr className="border-aap-gray-200 py-3" />
                            <div>
                                <div className="phoneNum flex">
                                    <a
                                        type="button"
                                        className="bg-aap-green-500 hover:bg-aap-green-600 py-3 px-5 mb-3 rounded-md w-full text-center text-white block no-underline"
                                        href={`sms:&body=${props.translations.smsMessageBody} ${encodeURIComponent(
                                            planUrl
                                        )}`}
                                        id={`btn_smsMediaPlan_${ELCID[props.lcid]}`}
                                    >
                                        <MdSms className="inline" size={24} /> {props.translations.sendAsTextButton}
                                    </a>
                                </div>
                            </div>
                        </div>
                    ) : !props.readonly ? (
                        <div className="email basis-1/2 p-5 text-center">
                            {props.isOnline
                                ? props.translations.loginOrRegisterToSend
                                : props.translations.connectToInternetToSend}
                        </div>
                    ) : null}
                </div>
                <div>
                    <div className="tabs flex gap-2 border-b border-b-aap-gray-500 print:hidden">
                        {props.family.members.map((member) => (
                            <div
                                key={`membertab-${member.name}`}
                                className={`p-2 flex-grow-0 basis-32 rounded-t-md text-center text-sm cursor-pointer ${
                                    member.name === activeMember.name
                                        ? `bg-aap-green-500 text-white`
                                        : `bg-aap-gray-200 hover:bg-aap-gray-300`
                                }`}
                                onClick={() => {
                                    setActiveMember(member);
                                }}
                            >
                                {member.name}
                            </div>
                        ))}
                    </div>
                    {props.family.members.map((member) => (
                        <div
                            className={`printedPage my-5 p-8 border border-aap-gray-500 bg-white shadow-lg ${
                                activeMember.name === member.name && activeMember.age === member.age
                                    ? `block`
                                    : `hidden`
                            } print:block print:shadow-none print:border-0 print:p-0`}
                            key={`membersummary-${member.name}-${member.age}`}
                        >
                            <div className="header pb-5 md:flex print:flex justify-between">
                                <div className="flex-grow-0 pt-2 logo w-64 order-2 mb-3 md:mb-0">
                                    <img
                                        src={props.lcid === ELCID.English ? `/_layouts/15/AAP.HealthyChildren/images/healthy-children-logo-transparent.png` : `/_layouts/15/AAP.HealthyChildren/3082/images/healthy-children-logo-transparent.png`}
                                        className="w-full h-auto"
                                        alt="HealthyChildren Logo"
                                    />
                                </div>
                                <div className="avatar flex gap-2 h-15 order-1">
                                    <Avatar
                                        avatarId={member.avatar}
                                        uniqueKey={`printout-${member.name}-avatar`}
                                        size={64}
                                        additionalClasses={`bg-white`}
                                        lcid={props.lcid}
                                        translations={props.translations}
                                    />
                                    <div className="text-left" title={member.age?.toString()}>
                                        <div className="pt-2 text-lg font-semibold">{member.name}</div>
                                        <div className="text-xs whitespace-nowrap">{props.translations[member.age as string]}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="underline h-1.5 border-t border-b border-aap-gray-500"></div>
                            <div className="planBody pt-5">
                                <h2 className="text-3xl print:text-lg mb-3 print:mb-1">
                                    {props.translations.userMediaPlanTitle.replace(
                                        "{{FAMILYNAME}}",
                                        props.family.name
                                    )}
                                </h2>
                                {props.topics.map((topic) => {
                                    if (props.family.priorities.indexOf(topic.id) > -1) {
                                        return (
                                            <div
                                                className="topicWrap"
                                                key={`topicWrap-${topic.id}`}
                                            >
                                                {topic.promises.map((promise) => {
                                                    const activePoints = (
                                                        promise.allPoints || []
                                                    ).filter((promisePoint) =>
                                                        shouldPointBeSelected(member, promisePoint)
                                                    );
                                                    const customPoints = (props.family.customPoints || [])
                                                        .filter(
                                                            (customPoint) =>
                                                                customPoint.promiseId === promise.id &&
                                                                customPoint.topicId === topic.id
                                                        )
                                                        .filter((customPoint) =>
                                                            shouldPointBeSelected(member, customPoint)
                                                        );

                                                    const allPoints = [...activePoints, ...customPoints];

                                                    if (allPoints.length === 0) {
                                                        return null;
                                                    }

                                                    return (
                                                        <div
                                                            className="promiseWrap my-8 last-of-type:mb-0 break-inside-avoid"
                                                            key={`promiseWrap-${topic.id}-${promise.id}`}
                                                        >
                                                            <h3 className="text-lg mb-1 font-semibold print:text-sm underline">
                                                                {decodeURIComponent(promise.title)}
                                                            </h3>
                                                            <ul className="list-none m-0 p-0 ml-5">
                                                                {allPoints.map((promisePoint) => {
                                                                    const pointTitle = decodeURIComponent(
                                                                        promisePoint.title
                                                                    );
                                                                    const pointDescription = decodeURIComponent(
                                                                        promisePoint.description || ""
                                                                    );
                                                                    const pointAdditionalInfo = decodeURIComponent(
                                                                        promisePoint.additionalInfo || ""
                                                                    );

                                                                    return (
                                                                        <li
                                                                            className="py-1 ml-0.5 pl-0 mb-3 flex text-sm print:text-xs break-inside-avoid"
                                                                            key={`promisePoint-${pointTitle}`}
                                                                        >
                                                                            <div className="inline-block w-3.5 h-3.5 bg-aap-red-500 print:bg-black shadow-md shadow-aap-gray-500 rounded-full mr-2 flex-shrink-0 flex-grow-0 my-0.5 print-exact"></div>
                                                                            <div>
                                                                                <div className="font-semibold text-md print:text-base">
                                                                                    {pointTitle}
                                                                                </div>
                                                                                {/* <div className="flex justify-evenly gap-5 px-3">
                                                                                    {pointDescription ? (
                                                                                        <div className="py-3 basis-1/2 grow shrink-0">
                                                                                            <h5 className="font-semibold text-xs mb-2">
                                                                                                {props.scienceTitle ||
                                                                                                    "The science behind it..."}
                                                                                            </h5>
                                                                                            <p
                                                                                                className="text-xs finishpage-moreinfo"
                                                                                                dangerouslySetInnerHTML={{
                                                                                                    __html: pointDescription,
                                                                                                }}
                                                                                            ></p>
                                                                                        </div>
                                                                                    ) : null}
                                                                                    {pointAdditionalInfo ? (
                                                                                        <div className="py-3 basis-1/2 grow shrink-0">
                                                                                            <h5 className="font-semibold text-xs mb-2">
                                                                                                {props.talktoTitle ||
                                                                                                    "What to say to my kids..."}
                                                                                            </h5>
                                                                                            <p
                                                                                                className="text-xs finishpage-moreinfo"
                                                                                                dangerouslySetInnerHTML={{
                                                                                                    __html: pointAdditionalInfo,
                                                                                                }}
                                                                                            ></p>
                                                                                        </div>
                                                                                    ) : null}
                                                                                </div> */}
                                                                            </div>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    }

                                    return null;
                                })}
                            </div>
                            {/* <div
                                className={`planFooter mt-5 -mb-5 border-t border-aap-gray-500 ${
                                    props.globalFooter ? `` : `flex justify-end`
                                }`}
                            >
                                {props.globalFooter ? (
                                    <div
                                        className="clearfix"
                                        dangerouslySetInnerHTML={{ __html: props.globalFooter }}
                                    />
                                ) : (
                                    <div className="aap-logo w-64 py-3">
                                        <img src={`/_catalogs/masterpage/Assets/images/logo-aap.png`} alt="AAP Logo" />
                                    </div>
                                )}
                            </div> */}
                        </div>
                    ))}
                </div>
                {props.footer ? (
                    <div className="pt-10">
                        <div dangerouslySetInnerHTML={{ __html: props.footer || "" }}></div>
                    </div>
                ) : null}
                <div className="mobileNav py-2 flex justify-between print:hidden lg:hidden">
                    {props.backButton}
                    {props.continueButton}
                </div>
            </div>
        </>
    );
};
